// Smart resolution suggestions for merge conflicts (#10). Conservative and side-presence-driven:
// we only suggest when one branch deleted a node and the other edited/kept it (a real edit should
// not be silently lost to a delete), or when both branches deleted it. When both sides made
// substantive different edits we return null — that genuine divergence needs the user's judgement,
// and guessing there would be worse than no suggestion.

/** Minimal shape of a conflict region needed to reason about a suggestion (decoupled from the DTO). */
export interface ConflictRegionLike {
  oursMissing?: boolean;
  theirsMissing?: boolean;
  baseMissing?: boolean;
  isResolved?: boolean;
  conflictType?: string;
}

export interface ConflictSuggestion {
  /** Which side to keep. "target" = ours, "source" = theirs (matches the rest of the merge UI). */
  side: "target" | "source";
  reason: string;
}

const KEEP_EDIT_REASON = "The other branch deleted this; keeping the edit preserves it.";
const BOTH_DELETED_REASON = "Both branches removed this — it will be removed.";

/**
 * Returns a confident resolution suggestion for a single region, or null when the branches diverged
 * in a way we shouldn't guess about. A resolved region never yields a suggestion.
 */
export function suggestRegionResolution(region: ConflictRegionLike | null | undefined): ConflictSuggestion | null {
  if (!region || region.isResolved) return null;

  const oursMissing: boolean = !!region.oursMissing; // target side deleted this node
  const theirsMissing: boolean = !!region.theirsMissing; // source side deleted this node

  // Target deleted, source still has it → keep the source edit.
  if (oursMissing && !theirsMissing) {
    return { side: "source", reason: KEEP_EDIT_REASON };
  }

  // Source deleted, target still has it → keep the target edit.
  if (!oursMissing && theirsMissing) {
    return { side: "target", reason: KEEP_EDIT_REASON };
  }

  // Both deleted → either side yields the same delete; suggest one deterministically.
  if (oursMissing && theirsMissing) {
    return { side: "target", reason: BOTH_DELETED_REASON };
  }

  // Both sides present and edited (edit-vs-edit / add-vs-add) → genuine divergence, no guess.
  return null;
}
